import { DollarSign } from "lucide-react";
import ToggleSwitch from "../ui/ToggleSwitch";
import { PALETTE } from "../../styles/configStyles";
import EffectiveDateBadge from "../EffectiveDateBadge";

const AdvancementsTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  config,
  setConfig,
  canEditField,
  getFieldStyle,
  isCreatingNewConfig,
  isEditingArchive,
  unlockTab,
  resetTabToGlobal,
  getTabEffectiveDate,
  getBadgeMode,
}) => {
  const canEdit = canEditField("advancements");

  return (
    <>
      <div style={{ marginTop: "24px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
            <div style={styles.sectionIconBadge(PALETTE.violet.base)}>
              <DollarSign size={20} />
            </div>
            {t.advancements.title}
            {!canEdit && (
              <span style={{
                fontSize: "11px",
                fontWeight: "700",
                marginLeft: isRTL ? "0" : "12px",
                marginRight: isRTL ? "12px" : "0",
                padding: "4px 12px",
                borderRadius: "6px",
                background: `${PALETTE.amber.base}15`,
                color: PALETTE.amber.base,
                border: `1px solid ${PALETTE.amber.base}25`,
              }}>
                {language === "ar" ? "(للقراءة فقط)" : "(Read-only)"}
              </span>
            )}
          </h3>
          <EffectiveDateBadge
            date={getTabEffectiveDate ? getTabEffectiveDate("advancement") : config.advancementEffectiveDate}
            isRTL={isRTL}
            theme={theme}
            mode={getBadgeMode ? getBadgeMode("advancement") : "locked"}
            onUnlock={unlockTab ? () => unlockTab("advancement") : undefined}
            onReset={resetTabToGlobal ? () => resetTabToGlobal("advancement") : undefined}
            onDateChange={(date) => setConfig((prev) => ({ ...prev, advancementEffectiveDate: date }))}
          />
        </div>

        <div style={styles.toggleContainer}>
          <label style={styles.label}>{t.advancements.enableAdvancements}</label>
          <ToggleSwitch
            checked={config.advancementEnabled || false}
            onChange={(e) =>
              canEdit && setConfig({ ...config, advancementEnabled: e.target.checked })
            }
            disabled={!canEdit}
            theme={theme}
            accentColor={PALETTE.violet.base}
            isRTL={isRTL}
          />
        </div>

        {config.advancementEnabled && (
          <>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.minMonthsBeforeRequest}</label>
                <input
                  type="text"
                  min="0"
                  value={config.minMonthsOfService}
                  onChange={(e) =>
                    setConfig({ ...config, minMonthsOfService: e.target.value ? parseInt(e.target.value) : null })
                  }
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.maxAdvancementPercentage}</label>
                <input
                  type="text"
                  min="0"
                  max="100"
                  value={config.maxAdvancementPercentOfSalary}
                  onChange={(e) =>
                    setConfig({ ...config, maxAdvancementPercentOfSalary: e.target.value ? parseFloat(e.target.value) : null })
                  }
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.maxAdvancementAmount} (EGP)</label>
                <input
                  type="text"
                  min="0"
                  value={config.maxAdvancementAmount}
                  onChange={(e) =>
                    setConfig({ ...config, maxAdvancementAmount: e.target.value ? parseFloat(e.target.value) : null })
                  }
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.advancements.maxActiveAdvancements}
                  <span style={{ fontSize: "11px", color: "#6b7280", marginLeft: isRTL ? "0" : "8px", marginRight: isRTL ? "8px" : "0" }}>
                    {language === "ar" ? "(في نفس الوقت)" : "(Simultaneous)"}
                  </span>
                </label>
                <input
                  type="text"
                  min="1"
                  value={config.blockAdvancementIfPending ? 1 : (config.maxActiveAdvancements ?? 2)}
                  onChange={(e) => {
                    if (!canEdit || config.blockAdvancementIfPending) return;
                    const raw = e.target.value ? parseInt(e.target.value) : 1;
                    setConfig({ ...config, maxActiveAdvancements: Math.max(1, raw) });
                  }}
                  disabled={!canEdit || config.blockAdvancementIfPending}
                  style={getFieldStyle("advancements", styles.input)}
                />
                {config.blockAdvancementIfPending && (
                  <span style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px", display: "block" }}>
                    {language === "ar"
                      ? "مقفل على 1 - فعّل \"السماح بطلبات متعددة\" للتعديل"
                      : 'Locked to 1 — enable "Allow multiple requests" below to change'}
                  </span>
                )}
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.advancements.maxAdvancementsPerMonth}
                  <span style={{ fontSize: "11px", color: "#6b7280", marginLeft: isRTL ? "0" : "8px", marginRight: isRTL ? "8px" : "0" }}>
                    {language === "ar" ? "(معتمد/شهر)" : "(Approved/month)"}
                  </span>
                </label>
                <input
                  type="text"
                  min="1"
                  value={config.maxAdvancementsPerMonth ?? ""}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, maxAdvancementsPerMonth: e.target.value ? parseInt(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                  placeholder={language === "ar" ? "غير محدود" : "Unlimited"}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  {t.advancements.maxMonthlyAdvancementTotal} (EGP)
                  <span style={{ fontSize: "11px", color: "#6b7280", marginLeft: isRTL ? "0" : "8px", marginRight: isRTL ? "8px" : "0" }}>
                    {language === "ar" ? "(إجمالي/شهر)" : "(Total/month)"}
                  </span>
                </label>
                <input
                  type="text"
                  min="0"
                  step="0.01"
                  value={config.maxMonthlyAdvancementTotal ?? ""}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, maxMonthlyAdvancementTotal: e.target.value ? parseFloat(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                  placeholder={language === "ar" ? "غير محدود" : "Unlimited"}
                />
              </div>

              <div style={{ ...styles.formGroup, visibility: "hidden" }}>
                <label style={styles.label}>-</label>
                <input type="text" style={styles.input} disabled />
              </div>
            </div>

            <div style={{
              padding: "12px 16px",
              background: theme === "dark" ? "#1e3a5f" : "#eff6ff",
              border: `1px solid ${theme === "dark" ? "#2563eb" : "#bfdbfe"}`,
              borderRadius: "8px",
              marginTop: "12px",
              marginBottom: "16px",
              fontSize: "12px",
              color: theme === "dark" ? "#93c5fd" : "#1e40af",
              lineHeight: "1.6",
              direction: isRTL ? "rtl" : "ltr",
            }}>
              <strong style={{ display: "block", marginBottom: "6px" }}>
                {language === "ar" ? "توضيح الحدود:" : "Limits Explained:"}
              </strong>
              {language === "ar" ? (
                <>
                  <div>• <strong>السلف النشطة:</strong> إجمالي السلف المعتمدة/المصروفة في نفس الوقت</div>
                  <div>• <strong>السلف شهرياً:</strong> عدد السلف التي يمكن اعتمادها في شهر واحد</div>
                  <div>• <strong>الإجمالي الشهري:</strong> المبلغ الكلي الذي يمكن صرفه في شهر واحد</div>
                </>
              ) : (
                <>
                  <div>• <strong>Max Active:</strong> Total approved/disbursed at the same time</div>
                  <div>• <strong>Per Month:</strong> How many can be approved in one calendar month</div>
                  <div>• <strong>Monthly Total:</strong> Total amount that can be disbursed per month</div>
                </>
              )}
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.minInstallments}</label>
                <input
                  type="text"
                  min="1"
                  max="24"
                  value={config.minInstallments ?? 1}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, minInstallments: e.target.value ? parseInt(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.maxInstallments}</label>
                <input
                  type="text"
                  min="1"
                  max="24"
                  value={config.maxInstallments}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, maxInstallments: e.target.value ? parseInt(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.defaultInstallments}</label>
                <input
                  type="text"
                  min="1"
                  max="24"
                  value={config.defaultInstallments}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, defaultInstallments: e.target.value ? parseInt(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.autoApproveUnderAmount}</label>
                <input
                  type="text"
                  min="0"
                  step="0.01"
                  value={config.autoApproveUnderAmount ?? 0}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, autoApproveUnderAmount: e.target.value ? parseFloat(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                  placeholder="0 = disabled"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t.advancements.emergencyMaxAmount}</label>
                <input
                  type="text"
                  min="0"
                  step="0.01"
                  value={config.emergencyMaxAmount}
                  onChange={(e) =>
                    canEdit && setConfig({ ...config, emergencyMaxAmount: e.target.value ? parseFloat(e.target.value) : null })
                  }
                  disabled={!canEdit}
                  style={getFieldStyle("advancements", styles.input)}
                />
              </div>
            </div>

            <div style={styles.toggleContainer}>
              <label style={styles.label}>{t.advancements.allowMultipleRequests}</label>
              <ToggleSwitch
                checked={!config.blockAdvancementIfPending}
                onChange={(e) => {
                  if (!canEdit) return;
                  const allowMultiple = e.target.checked;
                  setConfig({
                    ...config,
                    blockAdvancementIfPending: !allowMultiple,
                    // Toggling off collapses "max active" back to 1 (only one
                    // advancement can be active at a time). Toggling on restores
                    // whatever multi-value was previously set, defaulting to 2.
                    maxActiveAdvancements: allowMultiple
                      ? (config.maxActiveAdvancements && config.maxActiveAdvancements > 1
                        ? config.maxActiveAdvancements
                        : 2)
                      : 1,
                  });
                }}
                disabled={!canEdit}
                theme={theme}
                accentColor={PALETTE.violet.base}
                isRTL={isRTL}
              />
            </div>

            <div style={styles.toggleContainer}>
              <label style={styles.label}>{t.advancements.allowEmergencyAdvancements}</label>
              <ToggleSwitch
                checked={config.allowEmergencyAdvancements}
                onChange={(e) =>
                  canEdit && setConfig({ ...config, allowEmergencyAdvancements: e.target.checked })
                }
                disabled={!canEdit}
                theme={theme}
                accentColor={PALETTE.violet.base}
                isRTL={isRTL}
              />
            </div>

            <div style={styles.toggleContainer}>
              <label style={styles.label}>{t.advancements.blockIfOverdue}</label>
              <ToggleSwitch
                checked={config.blockAdvancementIfOverdue}
                onChange={(e) =>
                  canEdit && setConfig({ ...config, blockAdvancementIfOverdue: e.target.checked })
                }
                disabled={!canEdit}
                theme={theme}
                accentColor={PALETTE.violet.base}
                isRTL={isRTL}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AdvancementsTab;